import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

// Configuración de Payku
const PAYKU_API_URL = import.meta.env.VITE_PAYKU_API_URL || 'https://app.payku.cl/api';
const PAYKU_PUBLIC_TOKEN = import.meta.env.VITE_PAYKU_PUBLIC_TOKEN;
const PAYKU_PRIVATE_TOKEN = import.meta.env.VITE_PAYKU_PRIVATE_TOKEN;

/**
 * Servicio especializado para validar pagos con Payku de manera estricta,
 * asegurando que solo se acrediten monedas cuando el pago ha sido completado correctamente.
 */
export class PaykuValidator {
  /**
   * Verifica el estado real del pago con la API de Payku
   * @param transactionIdInput ID de la transacción a verificar
   * @returns Resultado de la verificación con detalles
   */
  public static async verifyPaykuPaymentStatus(transactionIdInput: string): Promise<{
    success: boolean;
    status: 'success' | 'pending' | 'cancelled' | 'error' | 'expired' | 'failed';
    message: string;
    details?: any;
  }> {
    console.log(`[PAYKU-VALIDATOR] Verificando estado real del pago para ID ${transactionIdInput} con Payku API...`);
    
    const isPaykuId = transactionIdInput.startsWith('trx');
    const searchColumn = isPaykuId ? 'payku_transaction_key' : 'transaction_id';
    const searchValue = transactionIdInput;

    console.log(`[PAYKU-VALIDATOR] Buscando transacción en BD por columna '${searchColumn}' con valor '${searchValue}'`);

    // --- PASO 1: Obtener el ID de Payku (payku_transaction_key) y estado local desde nuestra BD (CON REINTENTOS) ---
    let paykuTxKey: string | null = null;
    let internalUUID: string | null = null;
    const maxRetries = 2;
    const retryDelay = 500;
    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
      try {
        const { data: dbTx, error: dbError } = await supabase
          .from('transactions')
          .select('payku_transaction_key, status, transaction_id') // Seleccionar también UUID
          .eq(searchColumn, searchValue) // <-- BÚSQUEDA DINÁMICA
          .maybeSingle();

        if (dbError) {
          console.error(`[PAYKU-VALIDATOR] Error al buscar transacción para ${searchValue} por ${searchColumn} (intento ${attempt}):`, dbError);
          if (attempt > maxRetries) {
            return { success: false, status: 'error', message: 'Error de BD persistente al buscar transacción.' };
          }
          await sleep(retryDelay);
          continue;
        }

        if (!dbTx) {
          console.warn(`[PAYKU-VALIDATOR] Transacción ${searchValue} (buscada por ${searchColumn}) no encontrada en BD (intento ${attempt}).`);
          // Si buscamos por Payku ID y no lo encontramos, NO significa que no exista la transacción
          // Podría ser un ID inválido o aún no registrado.
          // Devolvemos un error específico para este caso.
           return { success: false, status: 'error', message: 'Transacción no encontrada en registro local.' };
        }

        // Tenemos la transacción
        paykuTxKey = dbTx.payku_transaction_key;
        internalUUID = dbTx.transaction_id; // Guardar el UUID interno
        const localStatus = dbTx.status;

        if (paykuTxKey) {
          console.log(`[PAYKU-VALIDATOR] ID de Payku ${paykuTxKey} y UUID interno ${internalUUID} obtenidos de BD (intento ${attempt}).`);
          break;
        }
        
        // No tenemos la key. Si el estado ya NO es 'pending', no tiene sentido reintentar.
        if (localStatus !== 'pending') {
          console.error(`[PAYKU-VALIDATOR] No se encontró payku_transaction_key para ${searchValue} y el estado local es '${localStatus}' (intento ${attempt}). No se puede consultar.`);
          return { success: false, status: 'error', message: `ID de Payku no encontrado y estado local es ${localStatus}.` };
        }

        // Es 'pending' y no hay key. Reintentar si quedan intentos.
        if (attempt <= maxRetries) {
          console.warn(`[PAYKU-VALIDATOR] No se encontró payku_transaction_key para ${searchValue} (estado local: ${localStatus}) en intento ${attempt}. Reintentando en ${retryDelay}ms...`);
          await sleep(retryDelay);
        } else {
          // Último intento fallido
          console.error(`[PAYKU-VALIDATOR] No se encontró payku_transaction_key en BD para ${searchValue} después de ${maxRetries + 1} intentos (estado local: ${localStatus}). No se puede consultar Payku.`);
          return { success: false, status: 'error', message: 'ID de transacción de Payku no encontrado en registro local tras reintentos.' };
        }

      } catch (error) {
        console.error(`[PAYKU-VALIDATOR] Excepción al buscar transacción para ${searchValue} por ${searchColumn} (intento ${attempt}):`, error);
        if (attempt > maxRetries) {
           return { success: false, status: 'error', message: 'Excepción de BD persistente al buscar transacción.' };
        }
        await sleep(retryDelay);
      }
    } // Fin del bucle for

    // Si llegamos aquí y no tenemos paykuTxKey, manejar error
    if (!paykuTxKey) {
        console.error(`[PAYKU-VALIDATOR] No se pudo obtener paykuTxKey para ${searchValue} (UUID interno: ${internalUUID}).`);
        // Determinar el mensaje de error basado en si encontramos la transacción pero no la key
        const message = internalUUID 
          ? 'ID de transacción de Payku no encontrado en registro local tras reintentos.'
          : 'Transacción no encontrada en registro local.';
        return { success: false, status: 'error', message };
    }
    
    // --- PASO 2: Usar el ID de Payku para consultar la API ---
    const PAYKU_API_URL = import.meta.env.VITE_PAYKU_API_URL || 'https://app.payku.cl/api';
    // Cargar AMBOS tokens
    const PAYKU_PUBLIC_TOKEN = import.meta.env.VITE_PAYKU_PUBLIC_TOKEN;
    const PAYKU_PRIVATE_TOKEN = import.meta.env.VITE_PAYKU_PRIVATE_TOKEN;

    // <<--- CAMBIO: Usar token PÚBLICO para la consulta GET --- >>
    if (!PAYKU_PUBLIC_TOKEN) {
      console.error('[PAYKU-VALIDATOR] Token PÚBLICO de Payku no configurado.');
      return { success: false, status: 'error', message: 'Token Público Payku no configurado.' };
    }
    // Ya no necesitamos el token privado para ESTA llamada específica
    // if (!PAYKU_PRIVATE_TOKEN) { ... }

    // Ya no necesitamos normalizar aquí, usamos el ID de Payku directamente
    // const normalizedId = normalizeTransactionId(transactionId);
    const apiUrl = `${PAYKU_API_URL}/transaction/${paykuTxKey}`; // <--- USAR paykuTxKey

    console.log(`[PAYKU-VALIDATOR] Consultando API: ${apiUrl}`);

    try {
      // Construir headers con tokens de autenticación
      const headers: Record<string, string> = {
        'Accept': 'application/json',
        // <<--- CAMBIO: Usar token PÚBLICO para la autorización GET --- >>
        'Authorization': `Bearer ${PAYKU_PUBLIC_TOKEN}`
      };

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
      });

      const responseData = await response.json();
      console.log('[PAYKU-VALIDATOR] Respuesta completa de API Payku:', responseData);

      if (!response.ok) {
        console.error(`[PAYKU-VALIDATOR] Error al consultar API de Payku: ${response.status}`, responseData);
        const errorMessage = responseData?.message || responseData?.error || `Error HTTP ${response.status}`;
        // Manejar específicamente el 401 si aún ocurre
        if (response.status === 401) {
          return {
            success: false,
            status: 'error', // O 'failed' ? Depende de cómo quieras manejarlo
            message: 'Error 401: Autenticación fallida con la API de Payku. Verifica tu token privado.',
            details: responseData
          };
        }
        // Otros errores HTTP
        return {
          success: false,
          status: 'error', // O 'failed'
          message: `Error al consultar estado en Payku: ${errorMessage}`,
          details: responseData
        };
      }

      // Interpretar la respuesta exitosa de Payku
      // Asumiendo que la respuesta tiene una estructura como la vista en el Sandbox
      const transactionDetails = responseData.transaction || responseData; // Adaptar si la estructura es diferente
      const paykuStatus = (transactionDetails?.status || transactionDetails?.gateway_response?.status || 'unknown').toLowerCase();
      const message = transactionDetails?.message || transactionDetails?.gateway_response?.message || 'Estado obtenido de Payku.';

      console.log(`[PAYKU-VALIDATOR] Estado según Payku: ${paykuStatus}`);

      switch (paykuStatus) {
        case 'success':
        case 'completed': // Añadir posibles alias para éxito
          return { success: true, status: 'success', message, details: transactionDetails };
        case 'pending':
        case 'processing': // Añadir posibles alias para pendiente
          return { success: false, status: 'pending', message, details: transactionDetails };
        case 'cancelled':
        case 'voided': // Añadir posibles alias para cancelado
          return { success: false, status: 'cancelled', message, details: transactionDetails };
        case 'expired':
          return { success: false, status: 'expired', message, details: transactionDetails };
        case 'failed':
        case 'rejected': // Añadir posibles alias para fallido
          return { success: false, status: 'failed', message, details: transactionDetails };
        default:
          console.warn(`[PAYKU-VALIDATOR] Estado de Payku desconocido: ${paykuStatus}`);
          return { success: false, status: 'error', message: `Estado desconocido recibido de Payku: ${paykuStatus}`, details: transactionDetails };
      }

    } catch (error) {
      console.error('[PAYKU-VALIDATOR] Excepción durante la llamada API a Payku:', error);
      return {
        success: false,
        status: 'error',
        message: `Excepción al consultar Payku: ${error instanceof Error ? error.message : String(error)}`,
      };
    }
  }
  
  /**
   * Proceso completo de verificación y actualización de pago
   * Solo acredita monedas si se confirma que el pago fue completado en Payku
   */
  public static async processPayment(transactionIdInput: string): Promise<{
    success: boolean;
    status: string;
    message: string;
    details?: any;
    coinsAdded?: number;
  }> {
    console.log(`[PAYKU-VALIDATOR] Iniciando proceso completo de verificación para ${transactionIdInput}`);
    const isPaykuId = transactionIdInput.startsWith('trx');
    const searchColumn = isPaykuId ? 'payku_transaction_key' : 'transaction_id';
    const searchValue = transactionIdInput;
    let internalUUID: string | null = null; // Necesitaremos el UUID para acreditar

    try {
      // 1. Buscar transacción local (usando la columna correcta)
      const { data: localTransaction, error: localError } = await supabase
        .from('transactions')
        .select('*')
        .eq(searchColumn, searchValue)
        .maybeSingle();

      if (localError && localError.code !== 'PGRST116') {
        console.error('[PAYKU-VALIDATOR] Error al buscar transacción local:', localError);
        return {
          success: false,
          status: 'error',
          message: 'Error al buscar transacción en base de datos'
        };
      }
      
      if (!localTransaction) {
          console.warn(`[PAYKU-VALIDATOR] Transacción ${searchValue} (buscada por ${searchColumn}) no encontrada en BD.`);
          return { success: false, status: 'error', message: 'Transacción no encontrada en registro local.' };
      }
      
      internalUUID = localTransaction.transaction_id; // Guardar el UUID

      // Si la transacción ya fue marcada como exitosa, no hacer nada
      if (localTransaction && localTransaction.status === 'success') {
        console.log('[PAYKU-VALIDATOR] La transacción ya fue procesada exitosamente');
        return {
          success: true,
          status: 'already_processed',
          message: 'La transacción ya fue procesada exitosamente',
          details: localTransaction,
          coinsAdded: localTransaction.vcoins_amount
        };
      }
      
      // Si la transacción está marcada como cancelada, no continuar
      if (localTransaction && localTransaction.status === 'cancelled') {
        console.log('[PAYKU-VALIDATOR] La transacción ya fue marcada como cancelada');
        return {
          success: false,
          status: 'cancelled',
          message: 'La transacción fue cancelada y no puede ser procesada',
          details: localTransaction
        };
      }
      
      // 2. VERIFICACIÓN CRÍTICA: Consultar el estado real con Payku (usando el UUID interno)
      const paykuVerification = await this.verifyPaykuPaymentStatus(internalUUID); // <-- LLAMAR SIEMPRE CON UUID
      
      // Si el pago no fue exitoso según Payku, marcar como cancelado y no acreditar monedas
      if (!paykuVerification.success || paykuVerification.status !== 'success') {
        console.log(`[PAYKU-VALIDATOR] Pago no exitoso según Payku: ${paykuVerification.status}`);
        
        // Actualizar estado en nuestra base de datos si existe la transacción
        if (localTransaction) {
          const { error: updateError } = await supabase
            .from('transactions')
            .update({
              status: 'cancelled',
              updated_at: new Date().toISOString(),
              metadata: {
                ...localTransaction.metadata,
                payku_verification: paykuVerification,
                cancelled_at: new Date().toISOString()
              }
            })
            .eq('transaction_id', internalUUID);
          
          if (updateError) {
            console.error('[PAYKU-VALIDATOR] Error al actualizar transacción como cancelada:', updateError);
          }
        }
        
        // Registrar la transacción cancelada en localStorage para futuras referencias
        try {
          // Lista de transacciones canceladas
          const cancelledTxsStr = localStorage.getItem('cancelled_transactions');
          let cancelledTxs = [];
          
          if (cancelledTxsStr) {
            try {
              cancelledTxs = JSON.parse(cancelledTxsStr);
              if (!Array.isArray(cancelledTxs)) cancelledTxs = [];
            } catch {}
          }
          
          if (!cancelledTxs.includes(internalUUID)) {
            cancelledTxs.push(internalUUID);
            localStorage.setItem('cancelled_transactions', JSON.stringify(cancelledTxs));
          }
        } catch (storageError) {
          console.error('[PAYKU-VALIDATOR] Error al guardar en localStorage:', storageError);
        }
        
        return {
          success: false,
          status: paykuVerification.status,
          message: paykuVerification.message,
          details: paykuVerification.details
        };
      }
      
      // 3. El pago fue exitoso según Payku, proceder a acreditar monedas
      console.log('[PAYKU-VALIDATOR] Pago verificado como exitoso en Payku, procesando...');
      
      // Si no existe la transacción en nuestra base de datos, no podemos continuar
      if (!localTransaction) {
        console.error('[PAYKU-VALIDATOR] No se encontró la transacción en nuestra base de datos');
        return {
          success: false,
          status: 'error',
          message: 'La transacción no existe en nuestra base de datos, aunque fue exitosa en Payku'
        };
      }
      
      // Actualizar el estado de la transacción a 'success'
      const { error: updateError } = await supabase
        .from('transactions')
        .update({
          status: 'success',
          updated_at: new Date().toISOString(),
          metadata: {
            ...localTransaction.metadata,
            payku_verification: paykuVerification,
            completed_at: new Date().toISOString()
          }
        })
        .eq('transaction_id', internalUUID);
      
      if (updateError) {
        console.error('[PAYKU-VALIDATOR] Error al actualizar transacción como exitosa:', updateError);
        return {
          success: false,
          status: 'error',
          message: 'Error al actualizar el estado de la transacción'
        };
      }
      
      // 4. Acreditar monedas al usuario
      const creditResult = await this.creditCoinsToUser(
        localTransaction.user_id,
        localTransaction.vcoins_amount || 0,
        internalUUID // <-- Usar UUID interno
      );
      
      if (!creditResult.success) {
        return {
          success: false,
          status: 'error',
          message: 'Error al acreditar monedas: ' + creditResult.message
        };
      }
      
      // 5. Todo el proceso fue exitoso
      return {
        success: true,
        status: 'success',
        message: 'Pago verificado y monedas acreditadas correctamente',
        details: {
          transaction: localTransaction,
          payku: paykuVerification.details
        },
        coinsAdded: localTransaction.vcoins_amount
      };
    } catch (error) {
      console.error('[PAYKU-VALIDATOR] Error general en processPayment:', error);
      return {
        success: false,
        status: 'error',
        message: error instanceof Error ? error.message : 'Error desconocido al procesar pago'
      };
    }
  }
  
  /**
   * Acredita monedas al usuario y registra la transacción
   */
  private static async creditCoinsToUser(
    userId: string, 
    coinsAmount: number,
    transactionId: string
  ): Promise<{ success: boolean, message: string }> {
    try {
      console.log(`[PAYKU-VALIDATOR] Acreditando ${coinsAmount} monedas al usuario ${userId}`);
      
      // Obtener saldo actual
      const { data: userCoins, error: userCoinsError } = await supabase
        .from('user_coins')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (userCoinsError && userCoinsError.code !== 'PGRST116') {
        console.error('[PAYKU-VALIDATOR] Error al obtener saldo del usuario:', userCoinsError);
        return { success: false, message: 'Error al obtener saldo del usuario' };
      }
      
      // Calcular nuevo saldo
      let currentBalance = 0;
      
      if (userCoins) {
        // Determinar el saldo actual (puede estar en balance o coins)
        if ('balance' in userCoins && userCoins.balance !== null) {
          currentBalance = userCoins.balance;
        } else if ('coins' in userCoins && userCoins.coins !== null) {
          currentBalance = userCoins.coins;
        }
      }
      
      const newBalance = currentBalance + coinsAmount;
      
      // Actualizar o crear saldo de usuario
      if (userCoins) {
        const { error: updateError } = await supabase
          .from('user_coins')
          .update({
            balance: newBalance,
            coins: newBalance,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);
        
        if (updateError) {
          console.error('[PAYKU-VALIDATOR] Error al actualizar saldo:', updateError);
          return { success: false, message: 'Error al actualizar saldo del usuario' };
        }
      } else {
        const { error: createError } = await supabase
          .from('user_coins')
          .insert({
            user_id: userId,
            balance: coinsAmount,
            coins: coinsAmount,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        
        if (createError) {
          console.error('[PAYKU-VALIDATOR] Error al crear saldo del usuario:', createError);
          return { success: false, message: 'Error al crear saldo del usuario' };
        }
      }
      
      // Registrar en el historial de transacciones (¡TABLA CORRECTA!)
      const { error: historyError } = await supabase
        .from('coin_transactions') // <<-- CORREGIDO DE 'coin_history'
        .insert({
          user_id: userId,
          amount: coinsAmount,
          description: `Compra de ${coinsAmount} VCoins (Payku)`, // Ajustar descripción si es necesario
          type: 'purchase',
          transaction_id: transactionId, // Enlazar con la transacción principal
          status: 'completed' // Asegurarse de incluir el estado
        });

      if (historyError) {
        if (historyError.code === '23505') { // Error de constraint única (duplicado)
          console.warn(`[PAYKU-VALIDATOR] Intento de registrar en coin_transactions duplicado para transaction_id ${transactionId}. Probablemente ya fue registrado por un proceso concurrente. Continuando...`);
        } else {
          console.error('[PAYKU-VALIDATOR] Error al registrar en coin_transactions:', historyError);
          // No debería bloquear la operación si el balance se actualizó
        }
      } else {
         console.log(`[PAYKU-VALIDATOR] Transacción registrada en coin_transactions para ${transactionId}`);
      }
      
      // Disparar evento para actualizar las monedas en el header
      window.dispatchEvent(new Event('coinsUpdated'));
      
      return { 
        success: true, 
        message: `Saldo actualizado correctamente a ${newBalance} monedas` 
      };
    } catch (error) {
      console.error('[PAYKU-VALIDATOR] Error al acreditar monedas:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Error desconocido al acreditar monedas' 
      };
    }
  }

  // Nueva función helper para actualizar estado local
  private static async updateLocalTransactionStatus(internalUUID: string, status: string): Promise<void> {
    if (!internalUUID) return;
    try {
        const { error } = await supabase
            .from('transactions')
            .update({ status: status, updated_at: new Date().toISOString() })
            .eq('transaction_id', internalUUID);
        if (error) {
            console.error(`[PAYKU-VALIDATOR] Error al actualizar estado local a ${status} para ${internalUUID}:`, error);
        }
    } catch (err) {
        console.error(`[PAYKU-VALIDATOR] Excepción al actualizar estado local para ${internalUUID}:`, err);
    }
  }
} 